import * as React from "react";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { ChevronDown } from "lucide-react";

import { cn } from "../../lib/utils";

const Accordion = AccordionPrimitive.Root;

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={cn("border-b border-lupa-border-soft", className)}
    {...props}
  />
));
AccordionItem.displayName = "AccordionItem";

type AccordionTriggerProps = React.ComponentPropsWithoutRef<
  typeof AccordionPrimitive.Trigger
> & {
  icon?: React.ReactNode;
  iconClassName?: string;
  iconExpanded?: boolean;
};

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  AccordionTriggerProps
>(({ className, children, icon, iconClassName, iconExpanded, ...props }, ref) => {
  const shouldRenderIcon = icon !== null;
  const iconNode = icon === undefined ? <ChevronDown className="h-4 w-4" /> : icon;

  return (
    <AccordionPrimitive.Header className="m-0 flex">
      <AccordionPrimitive.Trigger
        ref={ref}
        className={cn(
          "flex !flex-row w-full items-center py-4 text-start text-sm transition-colors hover:no-underline border-0 bg-transparent appearance-none rounded-none focus-visible:outline-none",
          shouldRenderIcon ? "justify-between" : "justify-start",
          className
        )}
        {...props}
      >
        {shouldRenderIcon && (
          <span
            className={cn(
              "accordion-expand-icon shrink-0 transition-transform duration-200",
              iconExpanded && "rotate-180",
              iconClassName
            )}
          >
            {iconNode}
          </span>
        )}
        {children}
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  );
});
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className={cn(
      "overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down",
      className
    )}
    {...props}
  >
    <div className="accordion-collapse-wrapper">
      <div className="accordion-collapse-inner">{children}</div>
    </div>
  </AccordionPrimitive.Content>
));
AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export { Accordion, AccordionContent, AccordionItem, AccordionTrigger };
